import { addDays, subDays, isAfter, isBefore } from "date-fns";

// Day index mapping: JS Date.getDay(): 0=Sun .. 6=Sat
export type WeekdayIndex = 0 | 1 | 2 | 3 | 4 | 5 | 6;

export type HolidayPredicate = (date: Date) => boolean;

export interface MultiGroupServicePattern {
  // `serviceCode` is never read here — only the group/day/array structure is —
  // so it stays optional to accept the parsed contract patterns, whose code can
  // be missing.
  [groupType: string]: Record<string, Array<{ serviceCode?: string }>>;
}

const dayStringToIndex: Record<string, WeekdayIndex> = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
};

export function collectSelectedWeekdays(
  servicePattern?: MultiGroupServicePattern
): WeekdayIndex[] {
  if (!servicePattern) return [];
  const set = new Set<WeekdayIndex>();
  for (const groupKey of Object.keys(servicePattern)) {
    const days = servicePattern[groupKey] || {};
    for (const dayKey of Object.keys(days)) {
      const idx = dayStringToIndex[dayKey.toLowerCase?.() || dayKey];
      if (idx !== undefined) {
        // Only include the day if at least one service is selected
        const services = days[dayKey];
        if (services && services.length > 0) set.add(idx);
      }
    }
  }
  return Array.from(set.values()).sort((a, b) => a - b);
}

export function findFirstWeekdayInRange(
  rangeStart: Date,
  rangeEnd: Date,
  allowedWeekdays: WeekdayIndex[],
  opts?: { isHoliday?: HolidayPredicate }
): Date | undefined {
  if (!rangeStart || !rangeEnd) return undefined;
  if (isAfter(rangeStart, rangeEnd)) return undefined;
  if (!allowedWeekdays || allowedWeekdays.length === 0) return undefined;

  const isHoliday = opts?.isHoliday;
  let d = new Date(rangeStart);
  while (!isAfter(d, rangeEnd)) {
    const isAllowed = allowedWeekdays.includes(d.getDay() as WeekdayIndex);
    if (isAllowed && !(isHoliday && isHoliday(d))) {
      return d;
    }
    d = addDays(d, 1);
  }
  return undefined;
}

export function findLastWeekdayInRange(
  rangeStart: Date,
  rangeEnd: Date,
  allowedWeekdays: WeekdayIndex[],
  opts?: { isHoliday?: HolidayPredicate }
): Date | undefined {
  if (!rangeStart || !rangeEnd) return undefined;
  if (isAfter(rangeStart, rangeEnd)) return undefined;
  if (!allowedWeekdays || allowedWeekdays.length === 0) return undefined;

  const isHoliday = opts?.isHoliday;
  let d = new Date(rangeEnd);
  while (!isBefore(d, rangeStart)) {
    const isAllowed = allowedWeekdays.includes(d.getDay() as WeekdayIndex);
    if (isAllowed && !(isHoliday && isHoliday(d))) {
      return d;
    }
    d = subDays(d, 1);
  }
  return undefined;
}

export function computeServiceBasedBillingPeriod(
  contractStart?: Date,
  contractEnd?: Date,
  servicePattern?: MultiGroupServicePattern,
  opts?: { isHoliday?: HolidayPredicate }
): { start?: Date; end?: Date } {
  if (!contractStart) return { start: undefined, end: undefined };
  const weekdays = collectSelectedWeekdays(servicePattern);
  if (weekdays.length === 0) {
    return { start: contractStart, end: contractEnd };
  }

  const start = contractEnd
    ? findFirstWeekdayInRange(contractStart, contractEnd, weekdays, opts)
    : undefined;

  const end = contractEnd
    ? findLastWeekdayInRange(contractStart, contractEnd, weekdays, opts)
    : undefined;

  return {
    start: start ?? contractStart,
    end: end ?? contractEnd,
  };
}

